// ds-allow-file: canvas palettes; literal colours mixed per frame (see below)
import type { VoiceStatus } from "./useOpusAgentVoice";

// Pure tuning for the voice orb: what each call state looks like, and how a
// frame's shape follows the audio. Kept apart from the canvas so it can be
// tested and tweaked without a browser.
//
// The palettes are literal colors on purpose: they paint a canvas
// illustration (gradients mixed per frame), which cannot take the Tailwind
// semantic tokens the product-code styling rule is about.

export type OrbPalette = {
  // Body gradient, lit corner to shadowed edge.
  core: string;
  mid: string;
  edge: string;
  // Soft light clouds that drift inside the body, blended with screen.
  clouds: [string, string, string, string];
  glow: string;
};

const LIVE: OrbPalette = {
  core: "#dff3ff",
  mid: "#4f8dff",
  edge: "#2a3fd6",
  clouds: [
    "rgba(255, 255, 255, 0.85)",
    "rgba(90, 200, 250, 0.75)",
    "rgba(139, 123, 255, 0.6)",
    "rgba(30, 64, 220, 0.7)",
  ],
  glow: "rgba(79, 141, 255, 0.45)",
};

const THINKING: OrbPalette = {
  core: "#f1e8ff",
  mid: "#8f6bff",
  edge: "#4b2fc9",
  clouds: [
    "rgba(255, 255, 255, 0.8)",
    "rgba(192, 132, 252, 0.75)",
    "rgba(90, 200, 250, 0.6)",
    "rgba(91, 33, 182, 0.7)",
  ],
  glow: "rgba(143, 107, 255, 0.45)",
};

const QUIET: OrbPalette = {
  core: "#f4f6f9",
  mid: "#b4bdca",
  edge: "#8793a4",
  clouds: [
    "rgba(255, 255, 255, 0.8)",
    "rgba(214, 220, 228, 0.7)",
    "rgba(174, 184, 198, 0.6)",
    "rgba(120, 132, 150, 0.5)",
  ],
  glow: "rgba(150, 162, 178, 0.3)",
};

export type OrbStyle = {
  palette: OrbPalette;
  // Radius multiplier before the level is added.
  baseScale: number;
  // How much the level grows the orb.
  levelScale: number;
  // Resting and level-driven edge wobble, as a fraction of the radius.
  baseWobble: number;
  levelWobble: number;
  // How fast the inner clouds drift, radians per second.
  spin: number;
  // Slow breathing when there is no audio to follow.
  breathe: number;
};

export const orbStyle = (status: VoiceStatus, muted: boolean): OrbStyle => {
  if (status === "connecting" || status === "idle")
    return {
      palette: QUIET,
      baseScale: 0.82,
      levelScale: 0,
      baseWobble: 0.015,
      levelWobble: 0,
      spin: 0.6,
      breathe: 0.05,
    };
  if (status === "thinking")
    return {
      palette: THINKING,
      baseScale: 0.9,
      levelScale: 0,
      baseWobble: 0.03,
      levelWobble: 0,
      spin: 2.4,
      breathe: 0.035,
    };
  if (status === "speaking")
    return {
      palette: LIVE,
      baseScale: 0.9,
      levelScale: 0.16,
      baseWobble: 0.02,
      levelWobble: 0.11,
      spin: 1.1,
      breathe: 0,
    };
  // Listening.
  return {
    palette: muted ? QUIET : LIVE,
    baseScale: 0.86,
    levelScale: muted ? 0 : 0.12,
    baseWobble: 0.018,
    levelWobble: muted ? 0 : 0.09,
    spin: 0.7,
    breathe: 0.02,
  };
};

// Whose voice the orb follows: the reply while speaking, the admin while
// listening (unless muted), nobody otherwise.
export const orbTarget = (
  status: VoiceStatus,
  muted: boolean,
  getInputLevel: () => number,
  getOutputLevel: () => number,
): number => {
  if (status === "speaking") return getOutputLevel();
  if (status === "listening" && !muted) return getInputLevel();
  return 0;
};

// The body's largest radius as a share of the canvas, leaving room for the
// halo (1.6 x the radius at full level) to fade out before the edge.
export const ORB_MAX_RADIUS_SHARE = 0.3;

// Fast attack, slow release: the orb jumps with a syllable and settles.
export const smoothLevel = (previous: number, target: number): number =>
  target > previous
    ? previous + (target - previous) * 0.5
    : previous + (target - previous) * 0.08;

export type OrbFrame = {
  radius: number;
  // Edge offset at angle theta, as a fraction of the radius.
  wobbleAt: (theta: number) => number;
};

export const orbFrame = (
  style: OrbStyle,
  level: number,
  seconds: number,
  maxRadius: number,
  reducedMotion: boolean,
): OrbFrame => {
  const clamped = Math.min(1, Math.max(0, level));
  const breathe = reducedMotion ? 0 : style.breathe * Math.sin(seconds * 2.2);
  const radius =
    maxRadius *
    Math.min(1, style.baseScale + style.levelScale * clamped + breathe);
  const amount = reducedMotion
    ? 0
    : style.baseWobble + style.levelWobble * clamped;
  return {
    radius,
    wobbleAt: (theta) =>
      amount *
      (0.55 * Math.sin(3 * theta + seconds * 1.3) +
        0.3 * Math.sin(5 * theta - seconds * 1.7) +
        0.15 * Math.sin(7 * theta + seconds * 2.3)),
  };
};

export type OrbCloud = {
  x: number;
  y: number;
  r: number;
  color: string;
  // Light clouds brighten (screen); the deep one adds shadow and depth.
  blend: "screen" | "source-over";
};

// Four soft clouds on their own Lissajous paths inside the body: the light
// ones drift near the top-left, the deep one sinks to the lower right. They
// swell with the level, and all motion stops for reduced motion.
const CLOUD_PATHS = [
  {
    orbit: 0.38,
    size: 0.62,
    speed: 0.9,
    ratio: 1.3,
    phase: 3.9,
    anchor: [-0.25, -0.3],
  },
  {
    orbit: 0.45,
    size: 0.55,
    speed: 1.2,
    ratio: 0.8,
    phase: 0.6,
    anchor: [0.1, -0.05],
  },
  {
    orbit: 0.5,
    size: 0.5,
    speed: -1.0,
    ratio: 1.6,
    phase: 2.2,
    anchor: [0.05, 0.15],
  },
  {
    orbit: 0.3,
    size: 0.7,
    speed: 0.7,
    ratio: 1.1,
    phase: 5.1,
    anchor: [0.3, 0.35],
  },
] as const;

export const orbClouds = (
  style: OrbStyle,
  level: number,
  seconds: number,
  center: number,
  radius: number,
  reducedMotion: boolean,
): OrbCloud[] =>
  CLOUD_PATHS.map((path, index) => {
    const t = reducedMotion ? 0 : seconds * style.spin * path.speed;
    const angle = t + path.phase;
    return {
      x: center + radius * (path.anchor[0] + path.orbit * Math.cos(angle)),
      y:
        center +
        radius * (path.anchor[1] + path.orbit * Math.sin(angle * path.ratio)),
      r: radius * (path.size + Math.min(1, Math.max(0, level)) * 0.25),
      color: style.palette.clouds[index],
      blend: index === CLOUD_PATHS.length - 1 ? "source-over" : "screen",
    };
  });
